import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../../integrations/supabase/client";
import { Callout } from "../../ui/Callout";
import { Hint } from "../../ui/Hint";
import { AuthFrame } from "./AuthFrame";

/**
 * The code from the authenticator app, asked for and answered.
 *
 * Moved out of `VerificationRoute` in build round 45b (2026-10-13) so the screen that sets a password
 * after a recovery or an invitation link can ask the same question in the same words. GoTrue refuses a
 * new password at aal1 on an account with a verified factor ("AAL2 session is required to update email
 * or password when MFA is enabled"), so a person who had forgotten their password and held an
 * authenticator could never set a new one: Mattej met it on staging that morning. Everything below
 * was `VerificationRoute`'s body, unchanged; the route keeps the gate, the loading and the account
 * with no factor, and so does `SetPasswordRoute`.
 *
 * The three failures it names rather than swallows: a wrong code, an expired challenge, and a
 * challenge that could not be taken. Every one ends in a sentence.
 */

/** GoTrue's challenge window is five minutes. Read from the answer rather than assumed. */
interface Challenge {
  id: string;
  expiresAt: number;
}

type Failure = "wrong-code" | "expired" | "no-challenge" | "unknown";

const FAILURE_TEXT: Record<Failure, string> = {
  "wrong-code":
    "Koden stämmer inte. Kontrollera att appen visar koden för det här kontot, och att telefonens klocka går rätt.",
  expired:
    "Koden hann gå ut. Ange den nya koden som appen visar nu.",
  "no-challenge":
    "Vi kunde inte starta kontrollen av koden. Försök igen, och kontrollera nätverket om det upprepas.",
  unknown: "Verifieringen gick inte igenom. Försök igen."
};

function classify(error: { code?: string; message?: string } | null): Failure {
  const code = error?.code ?? "";
  const message = (error?.message ?? "").toLowerCase();

  if (code === "mfa_challenge_expired" || message.includes("expired")) {
    return "expired";
  }
  if (code === "mfa_verification_failed" || message.includes("invalid")) {
    return "wrong-code";
  }
  return "unknown";
}

export interface CodeStepProps {
  factorId: string;
  /** The heading's one-line hint: why a code is asked for here. */
  hint: string;
  /** Called once GoTrue has accepted the code and the session is aal2. */
  onVerified: () => void;
  /** The way off the step, with its words. */
  cancel: { label: string; onClick: () => void };
  /** A line under the form, for what the step cannot fix. */
  footnote?: ReactNode;
}

export function CodeStep({ factorId, hint, onVerified, cancel, footnote }: CodeStepProps) {
  const supabase = getSupabaseBrowserClient();

  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const codeField = useRef<HTMLInputElement | null>(null);

  /**
   * The open challenge, as a query rather than as state filled in by an effect.
   *
   * A query because that is what it is: one server answer, fetched when the factor is known and
   * fetched again on demand. `refetch` is what takes a fresh one, and a fresh one is taken after
   * every failed attempt. A challenge that has just been answered wrongly is not worth reasoning
   * about as reusable, and re-asking removes a whole class of "the second attempt failed for a
   * different reason than the first" from this screen.
   *
   * `gcTime` is a minute rather than zero, and that is not a caching preference. With zero, React's
   * development double-mount unsubscribes the query between the two mounts, the entry is collected,
   * and the second mount asks the server for a second challenge nobody will use. GoTrue rate-limits
   * MFA challenges to fifteen a minute per account, so every visit to this screen was quietly
   * costing two. A minute is long enough to survive the double-mount and short enough that a stale
   * challenge is caught by the expiry check below rather than by a failed attempt.
   */
  const challengeQuery = useQuery<Challenge>({
    queryKey: ["mfa-challenge", factorId],
    staleTime: Infinity,
    gcTime: 60_000,
    /**
     * Retried, against the client-wide `retry: false`.
     *
     * Asking for a challenge is a write on the server's side, and two of them arriving for the same
     * factor at the same moment can deadlock each other: GoTrue answers one of them 500 and logs
     * `deadlock detected`. It resolves the instant it is asked again. Everything else on this screen
     * is better off failing loudly, but telling somebody their network is broken because two
     * browsers signed in at once would be a lie with a working fix one request away.
     */
    retry: 2,
    retryDelay: 400,
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.challenge({ factorId });
      if (error || !data) {
        throw error ?? new Error("PORTAL_MFA_CHALLENGE_FAILED");
      }
      return { id: data.id, expiresAt: data.expires_at ?? 0 };
    }
  });

  const challenge = challengeQuery.data ?? null;

  useEffect(() => {
    codeField.current?.focus();
  }, [factorId]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setPending(true);
    setFailure(null);

    try {
      if (!challenge) {
        setFailure("no-challenge");
        await challengeQuery.refetch();
        return;
      }

      /**
       * Checked here as well as by the server.
       *
       * Not a duplicate: the server returns the same shape for an expired challenge as for several
       * other things, and a code typed into a window that has already closed is the one case where
       * the interface can say exactly what happened instead of guessing from an error string.
       */
      if (challenge.expiresAt > 0 && Date.now() / 1000 >= challenge.expiresAt) {
        setFailure("expired");
        setCode("");
        await challengeQuery.refetch();
        return;
      }

      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: code.trim()
      });

      if (error) {
        setFailure(classify(error));
        setCode("");
        await challengeQuery.refetch();
        return;
      }

      // `verify` issues a new session at aal2 and the client persists it.
      onVerified();
    } finally {
      setPending(false);
    }
  }

  /**
   * Drawn after `verificationPage` in `prototype/kund/pages-auth.js`, approved 2026-09-24. The lead
   * under the heading became the heading's one-line hint, as the third rule in design.md section 3
   * asks; the field's label and the six boxes the code fills say the rest of what it used to say.
   */
  return (
    <AuthFrame titleId="verify-heading">
      <h2 id="verify-heading" className="k-auth-title">
        Ange koden
        <Hint>{hint}</Hint>
      </h2>

      <form onSubmit={onSubmit} noValidate>
        <div className="box-field">
          <label htmlFor="mfa-code">Kod från appen</label>
          <input
            id="mfa-code"
            ref={codeField}
            className="k-code"
            name="one-time-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="000000"
            required
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
          />
        </div>

        {failure ?? (challengeQuery.isError ? "no-challenge" : null) ? (
          <Callout tone="danger" role="alert" testId="mfa-error">
            {FAILURE_TEXT[failure ?? "no-challenge"]}
          </Callout>
        ) : null}

        {/*
          * Disabled until there is a challenge to answer.
          *
          * Not decoration. Taking a challenge is a round trip, and somebody who types six digits
          * faster than it comes back would otherwise submit against nothing and be told the
          * server could not be reached, which is both wrong and alarming. It is the code path
          * this screen's own suite kept hitting before the button was gated.
          */}
        <button
          className="button is-primary is-block"
          type="submit"
          disabled={pending || code.length < 6 || !challenge}
        >
          <span>{pending ? "Verifierar" : "Verifiera och fortsätt"}</span>
        </button>
      </form>

      {footnote}

      <div className="k-auth-links">
        <button type="button" className="k-auth-link" onClick={cancel.onClick}>
          {cancel.label}
        </button>
      </div>
    </AuthFrame>
  );
}
